import React, { useEffect, useState } from 'react';
import { ShieldIcon } from 'lucide-react';
import { LoadingScreen } from './LoadingScreen';
interface TwoFactorAuthProps {
  onSuccess: () => void;
  username: string;
}
export const TwoFactorAuth: React.FC<TwoFactorAuthProps> = ({
  onSuccess,
  username
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(60);
  // Timer for OTP expiration
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prevTime => prevTime > 0 ? prevTime - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  // Handle input change for OTP digits
  const handleChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value.charAt(0);
    }
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    // Auto-focus next input
    if (value !== '' && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  };
  // Handle key press for backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && code[index] === '' && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Validate code
    if (code.some(digit => digit === '')) {
      setError('Masukkan kode 6 digit');
      return;
    }
    // Simulate verification process
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      // Show loading screen
      setShowLoadingScreen(true);
    }, 1500);
  };
  // Show loading screen after successful verification
  if (showLoadingScreen) {
    return <LoadingScreen onComplete={onSuccess} data-id="element-153" />;
  }
  return <div className="min-h-screen flex flex-col bg-gradient-to-br from-dark-900 to-dark-800" data-id="element-154">
      {/* Top security banner */}
      <div className="bg-accent-red py-1 text-center" data-id="element-155">
        <p className="text-xs font-medium text-white" data-id="element-156">
          SISTEM TERKLASIFIKASI - AKSES TERBATAS
        </p>
      </div>
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6" data-id="element-157">
        <div className="w-full max-w-md" data-id="element-158">
          <div className="mb-6 sm:mb-8 flex flex-col items-center" data-id="element-159">
            <img src="https://cdn.magicpatterns.com/uploads/hFLsea95KTbfu8th3Tcctj/logo-BIN-NEW.png" alt="BIN Logo" className="h-16 sm:h-24 mb-3 sm:mb-4" data-id="element-160" />
          </div>
          <div className="bg-dark-700 rounded-lg shadow-xl p-4 sm:p-8 border border-dark-600" data-id="element-161">
            <div className="flex justify-center mb-3 sm:mb-4" data-id="element-162">
              <div className="p-2 sm:p-3 bg-primary bg-opacity-20 rounded-full" data-id="element-163">
                <ShieldIcon className="h-6 w-6 sm:h-8 sm:w-8 text-primary" data-id="element-164" />
              </div>
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-white mb-2 text-center" data-id="element-165">
              Verifikasi Dua Faktor
            </h2>
            <p className="text-center text-sm sm:text-base text-gray-300 mb-4 sm:mb-6 px-2" data-id="element-166">
              Kode verifikasi telah dikirim ke perangkat terdaftar untuk
              pengguna <strong data-id="element-167">{username}</strong>
            </p>
            {error && <div className="mb-4 bg-red-900/30 border border-red-800 text-red-200 px-3 sm:px-4 py-2 sm:py-3 rounded text-sm" data-id="element-168">
                {error}
              </div>}
            <form onSubmit={handleSubmit} data-id="element-169">
              <div className="mb-4 sm:mb-6" data-id="element-170">
                <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2 sm:mb-3 text-center" data-id="element-171">
                  Masukkan kode 6 digit
                </label>
                <div className="flex justify-center gap-1 sm:gap-2" data-id="element-172">
                  {code.map((digit, index) => <input key={index} id={`otp-${index}`} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleChange(index, e.target.value)} onKeyDown={e => handleKeyDown(index, e)} className="w-10 h-10 sm:w-12 sm:h-12 text-center bg-dark-800 border border-dark-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-white text-lg sm:text-xl" data-id="element-173" />)}
                </div>
                <div className="text-center mt-3 sm:mt-4 text-xs sm:text-sm text-gray-400" data-id="element-174">
                  Kode berlaku selama:{' '}
                  <span className="text-primary font-medium" data-id="element-175">
                    {Math.floor(timeLeft / 60)}:
                    {(timeLeft % 60).toString().padStart(2, '0')}
                  </span>
                </div>
              </div>
              <div data-id="element-176">
                <button type="submit" disabled={isLoading} className="w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-dark-900 bg-primary hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-177">
                  {isLoading ? <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-dark-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-178">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-179"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-180"></path>
                    </svg> : 'Verifikasi'}
                </button>
              </div>
              <div className="mt-3 sm:mt-4 text-center" data-id="element-181">
                <button type="button" className="text-primary hover:text-primary-light text-xs sm:text-sm transition-colors" onClick={() => setTimeLeft(60)} data-id="element-182">
                  Kirim ulang kode
                </button>
              </div>
            </form>
          </div>
          <p className="mt-3 sm:mt-4 text-center text-xs text-gray-400 px-4" data-id="element-183">
            Sistem ini diawasi dan dimonitor. Penggunaan tidak sah akan ditindak
            sesuai hukum yang berlaku.
          </p>
        </div>
      </div>
      {/* Footer */}
      <div className="py-2 sm:py-3 px-4 sm:px-6 bg-dark-900 border-t border-dark-700" data-id="element-184">
        <div className="text-center text-xs text-gray-500" data-id="element-185">
          &copy; {new Date().getFullYear()} Badan Intelijen Negara Republik
          Indonesia. Hak Cipta Dilindungi.
        </div>
      </div>
    </div>;
};